import React from 'react';
import classes from './NotesTab.module.scss';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setIsOpenAddNoteModal, setIsOpenNotesDeleteModal, setIsOpenNoteShareModal, setNotes, setSelectedNote } from 'store/slices/dataNotes';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import api from 'api';
import { Button, Checkbox, Empty } from 'antd';
import Spinner from 'components/Spinner/Spinner';
import Assets from 'assets/icons';
import NotesTable from './NotesTable';

type NotesTabProps = {
}

const NotesTab = ({}: NotesTabProps) => {  
  const [isDeletingNote, setIsDeletingNote] = React.useState<boolean>(false);
  const [visilbeTo, setVisilbeTo] = React.useState<string[]>([]);
  const [isSharingProcess, setIsSharingProcess]= React.useState(false);

  const { selectedDocument } = useAppSelector((state) => state.documents);
  const { users } = useAppSelector((state) => state.dataUsers);
  const { userData } = useAppSelector((state) => state.userData);
  const { messageApi } = useAppSelector((state) => state.windowState);
  const { isOpenNotesDeleteModal, isOpenNoteShareModal, selectedNote, notes, isNotesErorr, isNotesLoading } = useAppSelector((state) => state.notes);
  
  const showNotes = String((notes && notes[0]) ? true : false);
  const currentNote = notes?.find(note => note.id === selectedNote);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    if (isNotesErorr)  messageApi?.error(t('Documents.error.notesGet', { file: isNotesErorr }));
  }, [isNotesErorr]);

  const onDeleteNote = (id: string) => {
    dispatch(setIsOpenNotesDeleteModal(true));
    dispatch(setSelectedNote(id));
  };

  const onShareNote = (id: string) => {
    dispatch(setIsOpenNoteShareModal(true));
    dispatch(setSelectedNote(id));
    setVisilbeTo(notes?.find(note => note.id === id)?.visible_to || [])
  };

  const onModalsCancel = () => {
    dispatch(setIsOpenNotesDeleteModal(false));
    dispatch(setIsOpenNoteShareModal(false));
    dispatch(setSelectedNote(''));
    setVisilbeTo([]);
  };

  const itemClick = (user: any) => {    
    setVisilbeTo(prev => prev.includes(user.id) ? prev.filter(el => el !== user.id) : [...prev, user.id]);
  };  

  const shareUSers = users?.filter(user => user.id !== userData?.id).map(user => (
    <div className={classes.userLabelWrap} key={user.id}>
      <div>
        <div className={classes.labelName}>{`${user.first_name} ${user.last_name}`}</div>
        <div className={classes.labelEmail}>{user.email}</div>
      </div>
      <Checkbox
        onClick={() => itemClick(user)}
        checked={visilbeTo?.includes(user.id)}
      />
    </div>
  ));

  const handleShare = async () => {
    setIsSharingProcess(true);
    try {
      await api.shareNote({note_id: selectedNote, visible_to: visilbeTo})
      const newNotes = notes?.map(note => note.id === selectedNote
        ? { ...note, visible_to: visilbeTo }
        : note
      )
      dispatch(setNotes(newNotes!));
      messageApi?.success(t('Documents.details.notes.shareSuccess'));
    } catch (e) {
      messageApi?.error(t('Documents.details.notes.shareError'));
    } finally {
      dispatch(setSelectedNote(''));
      setIsSharingProcess(false);
      setVisilbeTo([]);
    }
  };

  const onDeleteSubmit = async () => {
    setIsDeletingNote(true);
    const currentNote = notes?.filter((note) => note.id === selectedNote)[0];
    
    const textToPopup = currentNote?.text && currentNote?.text.length > 50 ? currentNote?.text.slice(0, 50) + '...' : currentNote?.text 

    try {
      await api.deleteNote(selectedNote);
      const newNotes = notes?.filter((note) => note.id !== selectedNote);
      dispatch(setNotes(newNotes || []));
      messageApi?.success(
        `${t('Documents.success.deleteNote.before')} "${textToPopup}"
        ${t('Documents.success.deleteNote.after')}`
      );
    } catch (err) {
      messageApi?.error(
        `${t('Documents.error.deleteNote.before')} "${textToPopup}"
        ${t('Documents.error.deleteNote.after')}`
      );
    } finally {
      setIsDeletingNote(false);
      dispatch(setIsOpenNotesDeleteModal(false));
    }
  };

  const notesElement = {
    false: (
      <div className={classes.holder}>
        <Empty
          className={classes.noNotes}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>{t('Documents.details.notes.nullNotes')}</span>}
        />
        <Button
          icon={<Assets.NoteIcon />}
          className={classes.addNote}
          type='primary'
          onClick={() => dispatch(setIsOpenAddNoteModal(true))}
        >
          {t('Documents.details.notes.addNote')}
        </Button>
      </div>
    ),

    true: <NotesTable onDeleteClick={onDeleteNote} onShareNote={onShareNote}/>,
  };
  
  return (
    <>
      <div className={classes.notesWrap}>
        { isNotesLoading && !notes?.length
          ? <div className={classes.holder}><Spinner size={60} withLogo={true}/></div>
          : (
            <div style={{height: '100%'}}>
              {notesElement[showNotes as keyof typeof notesElement]}
            </div>
          )
        }
      </div>

      {isOpenNotesDeleteModal && (
        <ConfirmationModal
          isOpen={isOpenNotesDeleteModal}
          onCancel={onModalsCancel}
          isLoading={isDeletingNote}
          submit={onDeleteSubmit}
          text={`${t('Documents.modals.notes.deleteText.before')} "${selectedDocument?.name}" 
          ${t('Documents.modals.notes.deleteText.after')}`}
          title={t('Documents.modals.notes.deleteTitle')}
        />
      )}

      {isOpenNoteShareModal && (
        <ConfirmationModal
          isOpen={isOpenNoteShareModal}
          onCancel={onModalsCancel}
          okText={currentNote?.visible_to.length
            ? t('Documents.details.notes.change')
            : t('Documents.details.notes.share')
          }
          isLoading={isSharingProcess}
          submit={handleShare}
          text={shareUSers}
          title={t('Documents.modals.notes.shareTitle')}
          customClassname={'share'}
          okDisable={[...visilbeTo]?.sort().join() === [...currentNote?.visible_to || []]?.sort().join()}
        />
      )}
    </>
  );
}

export default NotesTab;
